import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import * as api from '@/data/api'
import { keys } from '@/api/hooks/shared/keys'

/**
 * The command palette's data read.
 *
 * Debouncing lives in the palette, not here: the hook must stay a plain
 * function of the text it is given, or two callers with different debounce
 * settings would produce two cache entries for the same query.
 *
 * The four options are all load-bearing:
 *   · `enabled` on a non-empty needle — an empty palette shows recents and
 *     actions, both client-side, and must not cost a round trip.
 *   · `placeholderData` keeps the previous result on screen while the next one
 *     lands. A palette that flashes empty between keystrokes is unusable.
 *   · `staleTime` because a search is not a money figure; re-running it on every
 *     focus change buys nothing.
 *   · `retry: false` because "search is unavailable" must appear immediately.
 *     A retried failure shows a spinner for two seconds and then the same error.
 */
export function useConsoleSearch(
  text: string,
  rootId?: string,
  /**
   * Narrow the SERVER's work, not just the rendered list. The palette searches
   * people only on a single character; filtering that out of the response left a
   * real `GET /v1/search` being asked to scan every market, runner and remark in
   * the open book so the caller could throw the answer away.
   */
  kinds?: api.SearchKind[],
): UseQueryResult<api.SearchResult> {
  const trimmed = text.trim()
  // `kinds` is already part of the cache key (whole-object), so two callers
  // asking for different kinds cannot share one another's truncated result.
  const query: api.SearchQuery = {
    text: trimmed,
    ...(rootId ? { rootId } : {}),
    ...(kinds && kinds.length > 0 ? { kinds } : {}),
  }

  return useQuery({
    queryKey: keys.search(query),
    queryFn: () => api.searchConsole(query),
    enabled: trimmed.length >= 1,
    placeholderData: (previous) => previous,
    staleTime: 10_000,
    gcTime: 60_000,
    retry: false,
  })
}
